import type {
    GetInsightFamiliesMetadataRequest,
    GetInsightFamiliesMetadataResponse,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import type { AsyncState, UseAsyncActions } from "@react-hookz/web";
import { useAsync } from "@react-hookz/web";
import { cloneDeep } from "lodash";

import { getCdbApiClient } from "../../api/cdbApi/cdbApi";
import { InsightFamiliesFilterField } from "../../types/filters/insightFamilies";
import type { InsightFamilyName } from "../../types/providers";
import {
    getInsightFamilyFilterQueryExpression,
    type InsightFamiliesFilters,
} from "../../utils/insightFamilies.utils";
import { useAsyncErrorStatusNotification } from "../notifications/useAsyncErrorStatusNotification";

export interface InsightFamiliesFiltersActionProps {
    advertiserId: string;
    marketplaceId: string;
}

export interface InsightFamiliesFiltersExecuteProps {
    ignoreField?: InsightFamiliesFilterField;
    ignoreFamily?: boolean;
}

export interface InsightFamiliesFiltersActionResponse {
    filters: InsightFamiliesFilters;
    families: InsightFamilyName[];
}

const emptyResponse: InsightFamiliesFiltersActionResponse = {
    filters: {
        [InsightFamiliesFilterField.BRAND]: {},
        [InsightFamiliesFilterField.BROWSE_NODE]: {},
        [InsightFamiliesFilterField.OBJECTIVE]: {},
    },
    families: [],
};

export function useGetInsightFamiliesFiltersAsync({
    advertiserId,
    marketplaceId,
}: InsightFamiliesFiltersActionProps): [
    AsyncState<InsightFamiliesFiltersActionResponse>,
    UseAsyncActions<
        InsightFamiliesFiltersActionResponse,
        [InsightFamiliesFiltersExecuteProps]
    >,
] {
    const [insightsState, insightsAction] = useAsync(
        async (props: InsightFamiliesFiltersExecuteProps) => {
            const { ignoreField, ignoreFamily } = props;
            const request: GetInsightFamiliesMetadataRequest = {
                advertiserId,
                marketplaceId,
                queryExpression: getInsightFamilyFilterQueryExpression(
                    ignoreField,
                    ignoreFamily,
                ),
            };

            const metadataResponse = await (
                await getCdbApiClient()
            ).getInsightFamiliesFilterMetadata(request);

            return {
                filters: generateFilterFromMetadata(metadataResponse),
                families:
                    metadataResponse.insightFamilies as InsightFamilyName[],
            };
        },
        emptyResponse,
    );

    useAsyncErrorStatusNotification(insightsState);

    return [insightsState, insightsAction];
}

const generateFilterFromMetadata = ({
    brands,
    browseNodeNames,
    objectives,
}: GetInsightFamiliesMetadataResponse): InsightFamiliesFilters => {
    const filters = cloneDeep(emptyResponse.filters);
    const brandNameToIdMap = (brands ?? []).reduce(
        (map, brand) => {
            if (brand.name && brand.id) {
                map[brand.name!] = brand.id;
            }
            return map;
        },
        {} as Record<string, string>,
    );

    // need to get ID from brand to parse
    [...new Set(brands?.map((b) => b.name!))].forEach((brand) => {
        filters[InsightFamiliesFilterField.BRAND][brand] = {
            label: brand,
            value: brand,
            group: brandNameToIdMap[brand]
                ? getGroupingFromBrandId(brandNameToIdMap[brand])
                : undefined,
        };
    });

    [...new Set(browseNodeNames?.map((b) => b.name!))].forEach(
        (brownseNode) => {
            filters[InsightFamiliesFilterField.BROWSE_NODE][brownseNode] = {
                label: brownseNode,
                value: brownseNode,
            };
        },
    );

    new Set(objectives).forEach((objective) => {
        filters[InsightFamiliesFilterField.OBJECTIVE][objective] = {
            label: objective,
            value: objective,
        };
    });

    return filters;
};

export enum InsightFamilyGroups {
    BRAND = "Brand",
    BUSINESSLINE = "Businessline",
}

/**
 * metadata attributes for insight families
 */
export enum InsightFamilyMetadataAttributes {
    DATASET_DATE = "datasetDate",
    UPLOAD_DATE = "uploadDate",
    END_DATE = "endDate",
    UPDATE_CADENCE = "updateCadence",
    START_DATE = "startDate",
    ICM_BRAND_VERSION = "icmBrandVersion",
    BRAND_TYPE = "brandType",
    BRAND_NAME = "brandName",
    BRAND_ID = "brandId",
    BROWSE_NODE_NAME = "browseNodeName",
    FAMILY_NAME = "familyName",
    OBJECTIVES = "objectives",
    FAMILY_TRACKING_ID = "familyTrackingId",
}

export enum InsightFamilyMetadataBrandType {
    ICM_BRAND = "ICM_BRAND",
    STANDARD = "STANDARD",
}

export const getGroupingFromBrandId = (
    brandId: string,
): InsightFamilyGroups => {
    if (brandId.indexOf("ICM|BUSINESS_LINE") === 0) {
        return InsightFamilyGroups.BUSINESSLINE;
    } else {
        return InsightFamilyGroups.BRAND;
    }
};
